import type { FolderTreeItem } from "@zapier/client";
import { TEMP_FOLDER_ID } from "@zapier/utils";

/**
 * Folder created inline during this Move-modal session. Kept in component
 * state (CAA pattern) so it stays pinned at the bottom of its parent after
 * folder-tree refetch/reorder, and so we can show a delete affordance.
 */
export interface SessionCreatedFolder {
  id: string;
  name: string;
  parentId: string | undefined;
  isShared: boolean;
  accountId: number;
  ownerId?: number;
  rootId?: string;
}

function stripFoldersById(
  folders: FolderTreeItem[],
  idsToStrip: Set<string>,
): FolderTreeItem[] {
  return folders
    .filter((folder) => !idsToStrip.has(String(folder.id)))
    .map((folder) => ({
      ...folder,
      children: stripFoldersById(
        (folder.children as FolderTreeItem[] | undefined) ?? [],
        idsToStrip,
      ),
    }));
}

/**
 * Strip server/optimistic copies of session-created ids (and TEMP optimistic
 * rows), then append session folders at the end of each parent's children.
 * Matches CAA: create does not inject a mid-list alphabetical row.
 */
export function pinSessionCreatedFolders(
  folders: FolderTreeItem[],
  sessionCreated: SessionCreatedFolder[],
): FolderTreeItem[] {
  // Always hide the create-mutation optimistic temp row so the list does not
  // jump while the pending input is still at the bottom (CAA stays still).
  const idsToStrip = new Set<string>([TEMP_FOLDER_ID]);
  for (const folder of sessionCreated) {
    idsToStrip.add(String(folder.id));
  }

  if (sessionCreated.length === 0) {
    return stripFoldersById(folders, idsToStrip);
  }

  const sessionByParent = new Map<string | undefined, SessionCreatedFolder[]>();
  for (const folder of sessionCreated) {
    const parentKey =
      folder.parentId === undefined || folder.parentId === null
        ? undefined
        : String(folder.parentId);
    const siblings = sessionByParent.get(parentKey);
    if (siblings) {
      siblings.push(folder);
    } else {
      sessionByParent.set(parentKey, [folder]);
    }
  }

  const mergeLevel = (
    serverChildren: FolderTreeItem[],
    parentId?: string,
  ): FolderTreeItem[] => {
    const stripped = serverChildren
      .filter((folder) => !idsToStrip.has(String(folder.id)))
      .map((folder) => ({
        ...folder,
        children: mergeLevel(
          (folder.children as FolderTreeItem[] | undefined) ?? [],
          String(folder.id),
        ),
      }));

    const sessionChildren = (sessionByParent.get(parentId) ?? []).map(
      (folder) => ({
        id: folder.id,
        name: folder.name,
        isShared: folder.isShared,
        accountId: folder.accountId,
        ownerId: folder.ownerId,
        parentId: folder.parentId,
        rootId: folder.rootId,
        children: mergeLevel([], String(folder.id)),
      }),
    );

    return [...stripped, ...sessionChildren];
  };

  return mergeLevel(folders);
}

/** Deepest-first session-created descendants (for cascade delete). */
export function collectSessionCreatedDescendants(
  rootId: string,
  sessionCreated: SessionCreatedFolder[],
): string[] {
  const byParent = new Map<string, SessionCreatedFolder[]>();
  for (const folder of sessionCreated) {
    if (folder.parentId == undefined) continue;
    const parentKey = String(folder.parentId);
    const siblings = byParent.get(parentKey);
    if (siblings) {
      siblings.push(folder);
    } else {
      byParent.set(parentKey, [folder]);
    }
  }

  const result: string[] = [];
  const visit = (id: string) => {
    for (const child of byParent.get(id) ?? []) {
      visit(String(child.id));
      result.push(String(child.id));
    }
  };
  visit(String(rootId));
  return result;
}
